import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAbility } from '@casl/react';
import {
    AlertTriangle, ArrowLeft, Ban, Camera, Check, CheckCircle2, Container, Expand, FileText,
    Loader2, MessageSquare, Paperclip, Pencil, Plus, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Combobox } from '../../components/ui/combobox';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import AccessRestricted from '../../components/AccessRestricted';
import { ImageLightbox } from '../../components/ui/image-lightbox';
import { AbilityContext } from '../../context/casl/AbilityContext';
import { ACTIONS, RESOURCES } from '../../constants/permissions.constants';
import { BranchContact } from '../../types/lists.types';
import {
    ImportOrderDetail, Product, StockDocument, StockOrderLine, StockReceipt, StockReceiptLine,
} from '../../types/stock.types';
import { importsApi, productsApi } from '../../utils/stock-api';
import api, { ApiError } from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { ImportStatusPill } from './ImportOrdersPage';
import SignaturePad from './components/SignaturePad';

const RECEIPT_STATUS_STYLES: Record<string, string> = {
    in_progress: 'text-blue-700 bg-blue-50',
    received: 'text-emerald-800 bg-emerald-100',
    voided: 'text-red-700 bg-red-50',
};

/**
 * Import Order detail — STOCK.md §6.
 *
 * The balance summary (§6.3) is the point of this screen: what was ordered, what has
 * actually turned up across every container, and what is still outstanding. Only
 * `received` receipts count toward it, which is what makes voiding one meaningful.
 *
 * Over-receipt is flagged amber, never blocked (§15.6) — short and over shipments happen,
 * and a system that refuses them just gets worked around.
 */
export default function ImportOrderDetailPage() {
    const ability = useAbility(AbilityContext);
    const canRead = ability.can(ACTIONS.READ, RESOURCES.STOCK_IMPORT_ORDER);
    const canAttachDocs = ability.can(ACTIONS.CREATE, RESOURCES.STOCK_PHOTO);
    const canDeleteDocs = ability.can(ACTIONS.DELETE, RESOURCES.STOCK_DOCUMENT);
    const { id, yardId } = useParams<{ id: string; yardId?: string }>();
    const navigate = useNavigate();
    const listHref = yardId ? `/stock/yards/${yardId}/imports` : '/stock/imports';

    const [order, setOrder] = useState<ImportOrderDetail | null>(null);
    /** Server-side refusal (yard scoping) — rendered as a page, not eternal skeletons (UAT B9). */
    const [accessDenied, setAccessDenied] = useState<string | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [users, setUsers] = useState<BranchContact[]>([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState<string | null>(null);

    // New receipt
    const [showReceiptForm, setShowReceiptForm] = useState(false);
    const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
    const [containerId, setContainerId] = useState('');
    const [receiptNumber, setReceiptNumber] = useState('');
    const [unloadedBy, setUnloadedBy] = useState<string>('');

    // Per-receipt line entry
    const [lineDrafts, setLineDrafts] = useState<Record<number, { productId: string; qty: string }>>({});
    /** The saved line whose quantity is being corrected (inline editor). */
    const [editingLine, setEditingLine] = useState<{ receiptId: number; lineId: number; qty: string } | null>(null);
    /** The line pending removal from its receipt — the receipt itself is preserved. */
    const [removingReceiptLine, setRemovingReceiptLine] = useState<{ receiptId: number; lineId: number; label: string } | null>(null);
    /** The receipt photo pending removal — photos lock once the receipt is received. */
    const [removingPhoto, setRemovingPhoto] = useState<{ receiptId: number; photoId: number } | null>(null);
    /** The in-progress receipt pending soft delete — for receipts added by accident. */
    const [deletingReceipt, setDeletingReceipt] = useState<StockReceipt | null>(null);
    // Add an unordered item to the import order (§6.3 escape hatch)
    const [showAddItem, setShowAddItem] = useState(false);
    const [addItemProductId, setAddItemProductId] = useState('');
    const [addItemQty, setAddItemQty] = useState('');

    // Edit / remove an existing order line — every change lands in the audit trail
    const [editingLineId, setEditingLineId] = useState<number | null>(null);
    const [editingQty, setEditingQty] = useState('');
    const [removingLine, setRemovingLine] = useState<StockOrderLine | null>(null);

    // Signing
    const [signing, setSigning] = useState<{ receiptId: number; role: 'receipt_1' | 'receipt_2' } | null>(null);
    const [signatureData, setSignatureData] = useState<string | null>(null);
    const [typedName, setTypedName] = useState('');

    // Void
    const [voiding, setVoiding] = useState<{ kind: 'order' | 'receipt'; id: number } | null>(null);
    const [voidReason, setVoidReason] = useState('');
    // Mark-as-received confirmation — completing the order is final
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const [comment, setComment] = useState('');
    /** Flattened across every receipt — arrows move between containers too. */
    const [photoIndex, setPhotoIndex] = useState<number | null>(null);
    const docInput = useRef<HTMLInputElement | null>(null);
    /** The source document pending removal (§12.4) — confirm before it goes. */
    const [docToDelete, setDocToDelete] = useState<StockDocument | null>(null);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            setOrder(await importsApi.get(Number(id)));
            setAccessDenied(null);
        } catch (err) {
            // A 403 here is yard scoping, not a transient failure — show it on the page
            // (the server's own wording) instead of leaving skeletons up forever (UAT B9).
            if (err instanceof ApiError && err.status === 403) {
                setAccessDenied(err.data?.message || err.message);
                return;
            }
            toast.error(err instanceof Error ? err.message : 'Failed to load import order');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { if (canRead) load(); }, [canRead, load]);

    useEffect(() => {
        if (!canRead) return;
        productsApi.list({ status: 'active' }).then(setProducts).catch(() => undefined);
        api.get<{ success: boolean; users: BranchContact[] }>('/api/users?all_users=true')
            .then((r) => setUsers(Array.isArray(r?.users) ? r.users : []))
            .catch(() => undefined);
    }, [canRead]);

    const run = async (key: string, fn: () => Promise<any>, message?: string) => {
        try {
            setBusy(key);
            await fn();
            if (message) toast.success(message);
            await load();
        } catch (err: any) {
            toast.error(err?.data?.message || err?.message || 'Something went wrong');
        } finally {
            setBusy(null);
        }
    };

    /** §12.4 — source paperwork can be attached at any status; there is no gate. */
    const handleDocuments = async (files: FileList | null) => {
        if (!files?.length || !order) return;
        await run('documents',
            () => importsApi.uploadDocuments(order.id, Array.from(files), 'upload'),
            'Document(s) attached');
    };

    if (!canRead) return <AccessRestricted />;

    if (accessDenied) {
        return <AccessRestricted accessMessage={accessDenied}
                                 backMessage="Back to Import Orders" navigateTo={listHref} />;
    }

    if (loading || !order) {
        return (
            <div className="space-y-3 animate-pulse p-2 md:p-0">
                {[0, 1, 2].map((i) => <div key={i} className="h-24 bg-gray-300 rounded-md" />)}
            </div>
        );
    }

    const isTerminal = order.status === 'closed' || order.status === 'voided';
    /** Balance rows still outstanding — used to warn before closing (§6.4). */
    const outstandingCount = order.balance.filter((b) => b.outstanding > 0).length;
    const draftFor = (rid: number) => lineDrafts[rid] ?? { productId: '', qty: '' };
    const setDraft = (rid: number, patch: Partial<{ productId: string; qty: string }>) =>
        setLineDrafts((prev) => ({ ...prev, [rid]: { ...draftFor(rid), ...patch } }));
    /** Persist a corrected line quantity; the change lands in the audit trail server-side. */
    const saveQty = (r: StockReceipt, l: StockReceiptLine) => {
        if (!editingLine) return;
        const qty = Number(editingLine.qty);
        if (!Number.isFinite(qty) || qty < 0) return;
        run(`editLine:${l.id}`, async () => {
            await importsApi.updateReceiptLine(order.id, r.id, l.id, { quantity: qty });
            setEditingLine(null);
        });
    };
    /** Attach a photo to an in-progress receipt; the server timestamps and hashes it (§12.1). */
    const uploadPhoto = (r: StockReceipt, file: File) =>
        run(`photo:${r.id}`, () => importsApi.uploadReceiptPhoto(order.id, r.id, file), 'Photo added');
    const canUpdateOrder = ability.can(ACTIONS.UPDATE, RESOURCES.STOCK_IMPORT_ORDER);
    /** Lines are editable until the order closes; the server enforces the same rule. */
    const linesEditable = !isTerminal && canUpdateOrder;
    /** Balance rows are keyed by product; the line actions need the line behind each one. */
    const lineByProductId = new Map(
        order.lines.filter((l) => l.product).map((l) => [l.product!.id, l]));
    /** The receipt-line picker offers only what is on the order; the dialog below is the escape hatch. */
    const orderProductOptions = order.lines
        .filter((l) => l.product)
        .map((l) => ({ label: `${l.product!.name} (${l.product!.sku})`, value: String(l.product!.id) }));
    const addItemProductOptions = products
        .filter((p) => !order.lines.some((l) => l.product?.id === p.id))
        .map((p) => ({ label: `${p.name} (${p.sku})`, value: String(p.id) }));

    const signatureFor = (r: StockReceipt, role: 'receipt_1' | 'receipt_2') =>
        r.signatures.find((s) => s.role === role) ?? null;
    const receiptPhotos = order.receipts.flatMap((r) =>
        r.photos.map((p) => ({
            id: p.id,
            url: p.url,
            label: r.containerId,
            caption: new Date(p.serverTimestamp).toLocaleString(),
        }))
    );

    return (
        <div className="space-y-4 md:space-y-6 p-2 md:p-0 max-w-5xl">
            {/* Header */}
            <div>
                <Button variant="ghost" size="sm" onClick={() => navigate(listHref)} className="mb-2 -ml-2">
                    <ArrowLeft size={16} className="mr-1.5" />Import Orders
                </Button>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <h1 className="text-2xl font-bold font-mono">{order.orderNumber}</h1>
                            <ImportStatusPill status={order.status} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                            {order.vendor?.tradingName}
                            {order.yard ? ` · ${order.yard.yardName}` : ''}
                            {order.expectedDate ? ` · expected ${order.expectedDate}` : ''}
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {order.status === 'receiving' && order.abilities.canApprove && (
                            <Button
                                onClick={() => setShowCloseConfirm(true)}
                                disabled={busy !== null}
                            >
                                <CheckCircle2 size={16} className="mr-2" />Mark as Received
                            </Button>
                        )}
                        {!isTerminal && order.abilities.canVoid && (
                            <Button
                                variant="outline"
                                className="text-destructive"
                                onClick={() => { setVoidReason(''); setVoiding({ kind: 'order', id: order.id }); }}
                                disabled={busy !== null}
                            >
                                <Ban size={16} className="mr-2" />Void
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {order.status === 'voided' && order.voidReason && (
                <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="p-4 text-sm">
                        <p className="font-medium text-red-800">Voided</p>
                        <p className="text-red-700 mt-1">{order.voidReason}</p>
                    </CardContent>
                </Card>
            )}

            {/* Balance summary — ordered qty is correctable here until the order closes */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between gap-3 mb-1">
                        <h2 className="font-semibold">Balance</h2>
                        {linesEditable && (
                            <Button variant="outline" size="sm" onClick={() => {
                                setAddItemProductId(''); setAddItemQty(''); setShowAddItem(true);
                            }}>
                                <Plus size={16} className="mr-1.5" />Add Item
                            </Button>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">
                        Ordered vs. actually received. Only signed-off receipts count — voided and
                        deleted ones are excluded. Item and quantity changes are written to the
                        audit trail.
                    </p>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="font-semibold text-slate-900">Product</TableHead>
                                    <TableHead className="font-semibold text-slate-900 text-right">Ordered</TableHead>
                                    <TableHead className="font-semibold text-slate-900 text-right">Received</TableHead>
                                    <TableHead className="font-semibold text-slate-900 text-right">Outstanding</TableHead>
                                    <TableHead className="font-semibold text-slate-900 text-right">%</TableHead>
                                    {linesEditable && (
                                        <TableHead className="font-semibold text-slate-900 text-right">Actions</TableHead>
                                    )}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {order.balance.map((b) => {
                                    const line = lineByProductId.get(b.productId);
                                    const editing = Boolean(line) && editingLineId === line!.id;
                                    return (
                                        <TableRow key={b.productId}>
                                            <TableCell>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span>{b.name}</span>
                                                    <span className="font-mono text-xs text-muted-foreground">{b.sku}</span>
                                                    {b.overReceived && (
                                                        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 ring-1 ring-amber-200 px-2 py-0.5 rounded-md">
                                                            <AlertTriangle size={11} />Over-received
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">
                                                {editing ? (
                                                    <div className="flex flex-col items-end gap-1">
                                                        <Input
                                                            type="number" inputMode="numeric"
                                                            min={b.receivedToDate > 0 ? b.receivedToDate : 1}
                                                            autoFocus
                                                            aria-label="Ordered quantity"
                                                            className="w-24 text-right"
                                                            value={editingQty}
                                                            onChange={(e) => setEditingQty(e.target.value)}
                                                        />
                                                        {Number(editingQty) > 0 && Number(editingQty) < b.receivedToDate && (
                                                            <span className="text-xs text-destructive whitespace-nowrap">
                                                                Cannot be below received ({b.receivedToDate})
                                                            </span>
                                                        )}
                                                    </div>
                                                ) : b.originalQuantity}
                                            </TableCell>
                                            <TableCell className="text-right tabular-nums">{b.receivedToDate}</TableCell>
                                            <TableCell className="text-right tabular-nums">{b.outstanding}</TableCell>
                                            <TableCell className="text-right tabular-nums">{b.percentReceived}%</TableCell>
                                            {linesEditable && (
                                                <TableCell className="text-right">
                                                    {editing ? (
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                variant="ghost" size="icon" aria-label="Save quantity"
                                                                disabled={busy !== null
                                                                    || !(Number(editingQty) > 0)
                                                                    || Number(editingQty) < b.receivedToDate}
                                                                onClick={() => line && run(`editLine:${line.id}`, async () => {
                                                                    await importsApi.updateLine(order.id, line.id, {
                                                                        quantity: Number(editingQty),
                                                                    });
                                                                    setEditingLineId(null);
                                                                }, 'Quantity updated')}
                                                            >
                                                                {busy === `editLine:${line!.id}`
                                                                    ? <Loader2 size={16} className="animate-spin" />
                                                                    : <Check size={16} />}
                                                            </Button>
                                                            <Button
                                                                variant="ghost" size="icon" aria-label="Cancel edit"
                                                                disabled={busy !== null}
                                                                onClick={() => setEditingLineId(null)}
                                                            >
                                                                <X size={16} />
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex justify-end gap-1">
                                                            <Button
                                                                variant="ghost" size="icon" aria-label="Edit quantity"
                                                                disabled={busy !== null || !line}
                                                                onClick={() => {
                                                                    if (!line) return;
                                                                    setEditingLineId(line.id);
                                                                    setEditingQty(String(line.originalQuantity ?? line.quantity));
                                                                }}
                                                            >
                                                                <Pencil size={16} />
                                                            </Button>
                                                            <Button
                                                                variant="ghost" size="icon" aria-label="Remove item"
                                                                className="text-destructive"
                                                                disabled={busy !== null || !line || b.receivedToDate > 0}
                                                                title={b.receivedToDate > 0
                                                                    ? 'Already received against — edit the quantity instead'
                                                                    : undefined}
                                                                onClick={() => line && setRemovingLine(line)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Receipts */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between gap-3 mb-4">
                        <h2 className="font-semibold flex items-center gap-2">
                            <Container size={18} />Receipts ({order.receipts.length})
                        </h2>
                        {!isTerminal && order.abilities.canReceipt && (
                            <Button variant="outline" size="sm" onClick={() => setShowReceiptForm((v) => !v)}>
                                <Plus size={16} className="mr-1.5" />Add Receipt
                            </Button>
                        )}
                    </div>

                    {showReceiptForm && (
                        <div className="border border-border rounded-lg p-4 mb-4 space-y-3 bg-muted/30">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <div>
                                    <Label htmlFor="r-date">Receipt Date *</Label>
                                    <Input id="r-date" type="date" value={receiptDate}
                                           onChange={(e) => setReceiptDate(e.target.value)} />
                                </div>
                                <div>
                                    <Label htmlFor="r-container">Container ID *</Label>
                                    <Input id="r-container" value={containerId}
                                           onChange={(e) => setContainerId(e.target.value)}
                                           placeholder="MSKU1234567" />
                                </div>
                                <div>
                                    <Label htmlFor="r-number">Stock Receipt No. *</Label>
                                    <Input id="r-number" value={receiptNumber}
                                           onChange={(e) => setReceiptNumber(e.target.value)} />
                                </div>
                            </div>
                            <div>
                                <Label htmlFor="r-unloaded">Unloaded By *</Label>
                                <Select value={unloadedBy} onValueChange={setUnloadedBy}>
                                    <SelectTrigger id="r-unloaded">
                                        <SelectValue placeholder="Select a person" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {users.map((u) => (
                                            <SelectItem key={u.id} value={String(u.id)}>{u.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex justify-end gap-2">
                                <Button variant="outline" size="sm" onClick={() => setShowReceiptForm(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    disabled={busy !== null || !containerId.trim() || !receiptNumber.trim() || !unloadedBy}
                                    onClick={() => run('addReceipt', async () => {
                                        await importsApi.addReceipt(order.id, {
                                            receiptDate,
                                            containerId: containerId.trim(),
                                            stockReceiptNumber: receiptNumber.trim(),
                                            unloadedBy: [Number(unloadedBy)],
                                        });
                                        setShowReceiptForm(false);
                                        setContainerId(''); setReceiptNumber(''); setUnloadedBy('');
                                    }, 'Receipt added')}
                                >
                                    {busy === 'addReceipt' && <Loader2 size={16} className="animate-spin mr-2" />}
                                    Add Receipt
                                </Button>
                            </div>
                        </div>
                    )}

                    {order.receipts.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">
                            Nothing has arrived yet.
                        </p>
                    ) : (
                        <div className="space-y-4">
                            {order.receipts.map((r) => {
                                const editable = r.status === 'in_progress';
                                const sig1 = signatureFor(r, 'receipt_1');
                                const sig2 = signatureFor(r, 'receipt_2');
                                const draft = draftFor(r.id);
                                /** The one signature a receipt needs — either legacy slot counts. */
                                const sig = sig1 ?? sig2;

                                return (
                                    <div key={r.id} className="border border-border rounded-lg p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                            <div>
                                                <span className="font-medium font-mono">{r.containerId}</span>
                                                <span className="text-sm text-muted-foreground ml-2">
                                                    {r.stockReceiptNumber} · {r.receiptDate}
                                                </span>
                                            </div>
                                            <span className={cn(
                                                'text-xs font-medium px-2 py-1 rounded-md capitalize',
                                                RECEIPT_STATUS_STYLES[r.status] ?? 'text-slate-700 bg-slate-100'
                                            )}>
                                                {r.status.replace(/_/g, ' ')}
                                            </span>
                                        </div>

                                        <p className="text-xs text-muted-foreground mb-3">
                                            Unloaded by {r.unloaders.map((u) => u.name).join(', ') || '–'}
                                            {' · '}{r.photos.length} photo(s)
                                        </p>
                                        {(r.photos.length > 0 || editable) && (
                                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
                                                {r.photos.map((p) => {
                                                    const flatIndex = receiptPhotos.findIndex((x) => x.id === p.id);
                                                    return (
                                                        <div key={p.id}
                                                             className="group relative rounded-md overflow-hidden border border-border">
                                                            <button type="button"
                                                                    onClick={() => setPhotoIndex(flatIndex)}
                                                                    aria-label="Open receipt photo"
                                                                    className="block w-full focus:outline-none focus:ring-2 focus:ring-ring">
                                                                <img src={p.url} alt="Receipt"
                                                                     className="aspect-square w-full object-cover transition-transform group-hover:scale-105" />
                                                                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center">
                                                                    <Expand size={18}
                                                                            className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                                                </span>
                                                            </button>
                                                            {editable && (
                                                                <button type="button"
                                                                        onClick={() => setRemovingPhoto({ receiptId: r.id, photoId: p.id })}
                                                                        aria-label="Remove photo"
                                                                        className="absolute top-1 right-1 h-6 w-6 rounded-md bg-black/60 text-white flex items-center justify-center hover:bg-destructive focus:outline-none focus:ring-2 focus:ring-ring">
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                                {editable && (
                                                    <label className={cn(
                                                        'aspect-square w-full rounded-md border border-dashed border-input cursor-pointer',
                                                        'flex flex-col items-center justify-center gap-1 text-xs text-muted-foreground hover:bg-accent',
                                                        busy !== null && 'opacity-50 pointer-events-none'
                                                    )}>
                                                        {busy === `photo:${r.id}`
                                                            ? <Loader2 size={16} className="animate-spin" />
                                                            : <Camera size={16} />}
                                                        Add Photo
                                                        <input
                                                            type="file" accept="image/*" capture="environment"
                                                            className="hidden"
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                e.target.value = '';
                                                                if (file) uploadPhoto(r, file);
                                                            }}
                                                        />
                                                    </label>
                                                )}
                                            </div>
                                        )}

                                        {/* Lines */}
                                        {r.lines.length > 0 && (
                                            <div className="divide-y divide-border border border-border rounded-md mb-3">
                                                {r.lines.map((l) => {
                                                    const editing = editingLine?.receiptId === r.id && editingLine?.lineId === l.id;
                                                    return (
                                                        <div key={l.id} className="flex items-center gap-3 p-2 text-sm">
                                                            <span className="flex-1 truncate">{l.product?.name}</span>
                                                            <span className="font-mono text-xs text-muted-foreground">
                                                                {l.product?.sku}
                                                            </span>
                                                            {editing ? (
                                                                <>
                                                                    <Input
                                                                        type="number" inputMode="decimal" min="0" step="any"
                                                                        autoFocus
                                                                        className="h-8 w-20 tabular-nums text-right"
                                                                        value={editingLine.qty}
                                                                        onChange={(e) => setEditingLine((p) => p ? { ...p, qty: e.target.value } : p)}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') saveQty(r, l);
                                                                            if (e.key === 'Escape') setEditingLine(null);
                                                                        }}
                                                                    />
                                                                    <Button size="icon" variant="ghost" className="h-8 w-8"
                                                                            disabled={busy !== null || editingLine.qty === ''}
                                                                            onClick={() => saveQty(r, l)}
                                                                            aria-label="Save quantity">
                                                                        <Check size={16} />
                                                                    </Button>
                                                                    <Button size="icon" variant="ghost" className="h-8 w-8"
                                                                            onClick={() => setEditingLine(null)}
                                                                            aria-label="Cancel edit">
                                                                        <X size={16} />
                                                                    </Button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <span className="tabular-nums font-medium w-16 text-right">
                                                                        {l.quantity === null
                                                                            ? <span className="text-muted-foreground italic">voided</span>
                                                                            : l.quantity}
                                                                    </span>
                                                                    {editable && l.quantity !== null && (
                                                                        <>
                                                                            <Button size="icon" variant="ghost" className="h-7 w-7"
                                                                                    onClick={() => setEditingLine({
                                                                                        receiptId: r.id, lineId: l.id, qty: String(l.quantity),
                                                                                    })}
                                                                                    aria-label="Edit quantity">
                                                                                <Pencil size={13} />
                                                                            </Button>
                                                                            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive"
                                                                                    onClick={() => setRemovingReceiptLine({
                                                                                        receiptId: r.id, lineId: l.id,
                                                                                        label: l.product?.name ?? 'Item',
                                                                                    })}
                                                                                    aria-label="Remove line">
                                                                                <Trash2 size={13} />
                                                                            </Button>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {editable && (
                                            <>
                                                <div className="grid grid-cols-[2fr_1fr_auto_auto] gap-2 mb-3">
                                                    <Combobox
                                                        options={orderProductOptions}
                                                        value={draft.productId}
                                                        onChange={(v) => setDraft(r.id, { productId: v ?? '' })}
                                                        placeholder="Select a product"
                                                        searchPlaceholder="Search products..."
                                                        emptyActionLabel={canUpdateOrder ? 'Add Item to Import Order' : undefined}
                                                        onEmptyAction={canUpdateOrder ? () => {
                                                            setAddItemProductId(''); setAddItemQty('');
                                                            setShowAddItem(true);
                                                        } : undefined}
                                                    />
                                                    <Input
                                                        type="number" inputMode="numeric" min="0" placeholder="Qty"
                                                        value={draft.qty}
                                                        onChange={(e) => setDraft(r.id, { qty: e.target.value })}
                                                    />
                                                    <Button
                                                        size="icon"
                                                        disabled={busy !== null || !draft.productId || !draft.qty}
                                                        onClick={() => run(`line:${r.id}`, async () => {
                                                            await importsApi.addReceiptLine(order.id, r.id, {
                                                                productId: Number(draft.productId),
                                                                quantity: Number(draft.qty),
                                                            });
                                                            setDraft(r.id, { productId: '', qty: '' });
                                                        })}
                                                    >
                                                        <Plus size={16} />
                                                    </Button>
                                                    <label className={cn(
                                                        'inline-flex items-center justify-center h-10 w-10 rounded-md border border-input cursor-pointer hover:bg-accent',
                                                        busy !== null && 'opacity-50 pointer-events-none'
                                                    )}>
                                                        <Camera size={16} />
                                                        <input
                                                            type="file" accept="image/*" capture="environment"
                                                            className="hidden"
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                e.target.value = '';
                                                                if (file) uploadPhoto(r, file);
                                                            }}
                                                        />
                                                    </label>
                                                </div>

                                                {/* §6.2 — one signature, but it is mandatory: a receipt
                                                    cannot be marked Received until the count is signed
                                                    off. Was two distinct signatories. */}
                                                <div className="border border-border rounded-md p-3 mb-3">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <p className="text-xs font-medium">Receipt signature</p>
                                                        {!sig && (
                                                            <span className="text-xs font-medium text-amber-700">
                                                                Required
                                                            </span>
                                                        )}
                                                    </div>
                                                    {sig ? (
                                                        <div className="flex items-center gap-3 mt-2">
                                                            {sig.signatureUrl && (
                                                                <img src={sig.signatureUrl} alt="Signature"
                                                                     className="h-16 border border-border rounded bg-white" />
                                                            )}
                                                            <div>
                                                                <p className="font-medium">{sig.typedName}</p>
                                                                <p className="text-xs text-muted-foreground">{sig.userName}</p>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <Button
                                                            variant="outline" size="sm" className="mt-2"
                                                            onClick={() => {
                                                                setSignatureData(null); setTypedName('');
                                                                setSigning({
                                                                    receiptId: r.id,
                                                                    role: sig1 ? 'receipt_1' : sig2 ? 'receipt_2' : 'receipt_1',
                                                                });
                                                            }}
                                                        >
                                                            Sign
                                                        </Button>
                                                    )}
                                                    <p className="text-xs text-muted-foreground mt-2">
                                                        Signature is mandatory — a receipt can't be marked Received
                                                        until the count is signed off.
                                                    </p>
                                                </div>

                                                <div className="flex flex-wrap justify-end gap-2">
                                                    <Button
                                                        variant="outline" size="sm" className="text-destructive"
                                                        disabled={busy !== null}
                                                        onClick={() => setDeletingReceipt(r)}
                                                    >
                                                        <Trash2 size={16} className="mr-1.5" />Delete Receipt
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        disabled={busy !== null || !sig}
                                                        onClick={() => run(`recv:${r.id}`,
                                                            () => importsApi.transitionReceipt(order.id, r.id, { to: 'received' }),
                                                            'Receipt received')}
                                                    >
                                                        <Check size={16} className="mr-1.5" />Mark this Item Received
                                                    </Button>
                                                </div>
                                            </>
                                        )}

                                        {r.status === 'received' && order.abilities.canVoid && (
                                            <div className="flex justify-end">
                                                <Button
                                                    variant="outline" size="sm" className="text-destructive"
                                                    onClick={() => { setVoidReason(''); setVoiding({ kind: 'receipt', id: r.id }); }}
                                                >
                                                    <Ban size={16} className="mr-1.5" />Void Receipt
                                                </Button>
                                            </div>
                                        )}

                                        {r.status === 'voided' && r.voidReason && (
                                            <p className="text-xs text-red-700 mt-2">
                                                Voided — {r.voidReason}. Quantities were snapshotted into the
                                                audit trail before being cleared.
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* §12.4 — source paperwork: PDFs/photos stored as the source of record,
                incl. the sheet the AI read. No status gate; links are re-signed per read. */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <h2 className="font-semibold flex items-center gap-2">
                            <Paperclip size={18} />Source documents
                        </h2>
                        {canAttachDocs && (
                            <>
                                <input
                                    ref={docInput}
                                    type="file"
                                    accept="application/pdf,image/*"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => {
                                        handleDocuments(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                                <Button variant="outline" size="sm"
                                        disabled={busy !== null}
                                        onClick={() => docInput.current?.click()}>
                                    {busy === 'documents'
                                        ? <Loader2 size={14} className="animate-spin mr-1.5" />
                                        : <Plus size={14} className="mr-1.5" />}
                                    Add documents
                                </Button>
                            </>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">
                        The paperwork behind this order, kept so the original is never lost.
                    </p>
                    {order.documents.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No source documents yet.</p>
                    ) : (
                        <div className="divide-y divide-border border border-border rounded-lg">
                            {order.documents.map((d) => (
                                <div key={d.id} className="flex items-center pr-1">
                                    <a href={d.url} target="_blank" rel="noreferrer"
                                       className="flex items-center gap-3 p-3 hover:bg-accent/40 transition-colors flex-1 min-w-0">
                                        <span className="w-10 h-10 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                                            <FileText size={16} className="text-muted-foreground" />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm font-medium truncate">{d.filename}</span>
                                            <span className="block text-xs text-muted-foreground">
                                                {d.uploadedBy?.name} · {new Date(d.createdAt).toLocaleString()}
                                            </span>
                                        </span>
                                        {d.source === 'ai_parse' && (
                                            <span className="text-[10px] font-medium text-blue-700 bg-blue-50 rounded px-1.5 py-0.5 shrink-0">
                                                AI sheet
                                            </span>
                                        )}
                                    </a>
                                    {canDeleteDocs && (
                                        <Button variant="ghost" size="icon"
                                                aria-label={`Remove ${d.filename}`}
                                                className="text-destructive shrink-0"
                                                disabled={busy !== null}
                                                onClick={() => setDocToDelete(d)}>
                                            <Trash2 size={16} />
                                        </Button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Comments */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold flex items-center gap-2 mb-3">
                        <MessageSquare size={18} />Comments
                    </h2>
                    <div className="flex flex-col sm:flex-row gap-2 mb-4">
                        <Textarea rows={2} placeholder="Add context…" value={comment}
                                  onChange={(e) => setComment(e.target.value)} />
                        <Button
                            disabled={busy !== null || !comment.trim()}
                            onClick={() => run('comment', async () => {
                                await importsApi.comment(order.id, comment.trim());
                                setComment('');
                            })}
                        >
                            Post
                        </Button>
                    </div>
                    {order.comments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No comments.</p>
                    ) : (
                        <div className="space-y-3">
                            {order.comments.map((c) => (
                                <div key={c.id} className="text-sm">
                                    <p className="font-medium">
                                        {c.user?.name}
                                        <span className="text-xs text-muted-foreground font-normal ml-2">
                                            {new Date(c.createdAt).toLocaleString()}
                                        </span>
                                    </p>
                                    <p className="text-muted-foreground whitespace-pre-wrap">{c.body}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Audit trail — order and receipt events interleaved */}
            <Card>
                <CardContent className="p-4 md:p-6">
                    <h2 className="font-semibold mb-3">Audit trail</h2>
                    <div className="space-y-2 text-sm">
                        {order.history.map((h) => {
                            const reason = h.reason;
                            // Evidence rows — item adds/edits/removals, receipt photos and
                            // signatures, comments — carry fromStatus === toStatus: the
                            // status did not change, the reason text says what happened
                            // (§12.3). Status moves, creation rows and receipt quantity
                            // pairs (the receipt's from/to hold before/after quantities)
                            // keep their arrow rendering.
                            const evidence = h.fromStatus !== null && h.fromStatus === h.toStatus;
                            const sep = evidence && reason ? reason.indexOf(' — ') : -1;
                            const kind = reason && sep > 0 ? reason.slice(0, sep) : null;
                            const detail = reason && sep > 0
                                ? reason.slice(sep + 3)
                                : (evidence && reason ? reason : null);
                            return (
                                <div key={`${h.ownerType}-${h.id}`} className="flex flex-wrap items-baseline gap-x-2">
                                    <span className="text-muted-foreground text-xs tabular-nums">
                                        {new Date(h.createdAt).toLocaleString()}
                                    </span>
                                    <span className="text-xs uppercase text-muted-foreground">{h.ownerType}</span>
                                    {evidence ? (
                                        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted rounded px-1.5 py-0.5">
                                            {kind ?? 'note'}
                                        </span>
                                    ) : (
                                        <span>
                                            {h.fromStatus
                                                ? `${String(h.fromStatus).replace(/_/g, ' ')} → ${String(h.toStatus).replace(/_/g, ' ')}`
                                                : `created as ${String(h.toStatus).replace(/_/g, ' ')}`}
                                        </span>
                                    )}
                                    <span className="text-muted-foreground">· {h.actorName}</span>
                                    {detail && <span className="text-muted-foreground w-full">{detail}</span>}
                                    {!evidence && h.reason && (
                                        <span className="text-muted-foreground w-full">{h.reason}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Mark-as-received confirmation — completing the order is final, and a
                short order is closed short (§6.4) */}
            <Dialog open={showCloseConfirm} onOpenChange={(o) => { if (!o) setShowCloseConfirm(false); }}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>
                            {outstandingCount > 0
                                ? `${order.orderNumber} is short`
                                : `Mark ${order.orderNumber} as received?`}
                        </DialogTitle>
                        <DialogDescription>
                            {outstandingCount > 0 ? (
                                <>
                                    <span className="font-medium text-amber-700">
                                        {outstandingCount} item{outstandingCount === 1 ? ' is' : 's are'} still short.
                                    </span>{' '}
                                    If you mark this as complete it will be closed short — the
                                    outstanding quantities stay unreceived.{' '}
                                    <span className="font-medium text-red-700">This cannot be undone.</span>
                                </>
                            ) : (
                                <>
                                    Everything on this order has been received. Once marked as
                                    received the order is closed and final — any changes will
                                    require a new import order.{' '}
                                    <span className="font-medium text-red-700">This cannot be undone.</span>
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    {outstandingCount > 0 && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            <p className="font-medium mb-1">Still outstanding:</p>
                            <ul className="list-disc pl-5 space-y-0.5">
                                {order.balance.filter((b) => b.outstanding > 0).map((b) => (
                                    <li key={b.productId}>
                                        {b.name} — {b.outstanding} {b.uom} short
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-2">
                                Consider adding another receipt, or correcting the ordered
                                quantities to match what actually arrived, before completing.
                            </p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCloseConfirm(false)}>Cancel</Button>
                        <Button
                            variant={outstandingCount > 0 ? 'destructive' : 'default'}
                            disabled={busy !== null}
                            onClick={async () => {
                                await run('close', () => importsApi.transition(order.id, { to: 'closed' }),
                                    outstandingCount > 0 ? 'Order closed short' : 'Order marked as received');
                                setShowCloseConfirm(false);
                            }}
                        >
                            {busy === 'close' && <Loader2 size={16} className="animate-spin mr-2" />}
                            <CheckCircle2 size={16} className="mr-2" />
                            {outstandingCount > 0 ? 'Close Short' : 'Mark as Received'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sign dialog */}
            <Dialog open={Boolean(signing)} onOpenChange={(o) => { if (!o) setSigning(null); }}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>Sign receipt</DialogTitle>
                        <DialogDescription>
                            The signature is bound to your logged-in account and shown on the record.
                            A receipt can't be marked Received without it.
                        </DialogDescription>
                    </DialogHeader>
                    <SignaturePad onChange={setSignatureData} />
                    <Input placeholder="Type your full name" value={typedName}
                           onChange={(e) => setTypedName(e.target.value)} />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSigning(null)}>Cancel</Button>
                        <Button
                            disabled={busy !== null || !signatureData || !typedName.trim()}
                            onClick={async () => {
                                if (!signing || !signatureData) return;
                                await run('sign', () => importsApi.signReceipt(order.id, signing.receiptId, {
                                    role: signing.role,
                                    signatureUrl: signatureData,
                                    typedName: typedName.trim(),
                                }), 'Signed');
                                setSigning(null);
                            }}
                        >
                            Sign
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Void dialog — shared by order and receipt */}
            <Dialog open={Boolean(voiding)} onOpenChange={(o) => { if (!o) setVoiding(null); }}>
                <DialogContent className="sm:max-w-[520px]">
                    <DialogHeader>
                        <DialogTitle>
                            Void {voiding?.kind === 'receipt' ? 'receipt' : order.orderNumber}
                        </DialogTitle>
                        <DialogDescription>
                            {voiding?.kind === 'receipt'
                                ? 'The quantities are snapshotted into the audit trail, then cleared so they stop counting toward the balance. Nothing is destroyed.'
                                : 'The full record is snapshotted into the audit trail before anything changes.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div>
                        <Label htmlFor="void-reason">Reason * (at least 20 characters)</Label>
                        <Textarea id="void-reason" rows={3} value={voidReason}
                                  onChange={(e) => setVoidReason(e.target.value)} />
                        <p className="text-xs text-muted-foreground mt-1">{voidReason.trim().length}/20</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVoiding(null)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            disabled={busy !== null || voidReason.trim().length < 20}
                            onClick={async () => {
                                if (!voiding) return;
                                const reason = voidReason.trim();
                                await run('void', () => voiding.kind === 'receipt'
                                    ? importsApi.transitionReceipt(order.id, voiding.id, { to: 'voided', reason })
                                    : importsApi.transition(order.id, { to: 'voided', reason }),
                                    'Voided');
                                setVoiding(null);
                            }}
                        >
                            <Ban size={16} className="mr-2" />Void
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            {/* Remove item confirm — the removal itself is written to the audit trail */}
            <Dialog open={Boolean(removingLine)} onOpenChange={(o) => { if (!o) setRemovingLine(null); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Remove {removingLine?.product?.name}?</DialogTitle>
                        <DialogDescription>
                            The item comes off {order.orderNumber}. The removal — item, ordered
                            quantity and who did it — stays visible in the audit trail. Once any
                            of it has been received it can no longer be removed; edit the
                            quantity instead.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRemovingLine(null)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            disabled={busy !== null}
                            onClick={async () => {
                                if (!removingLine) return;  
                                await run(`removeLine:${removingLine.id}`,
                                    () => importsApi.removeLine(order.id, removingLine.id),
                                    'Item removed');
                                setRemovingLine(null);
                            }}
                        >
                            {busy === `removeLine:${removingLine?.id}` && (
                                <Loader2 size={16} className="animate-spin mr-2" />
                            )}
                            Remove Item
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

                          
            {/* Remove line dialog — the receipt itself is preserved */}
            <Dialog open={Boolean(removingReceiptLine)} onOpenChange={(o) => { if (!o) setRemovingReceiptLine(null); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Remove line from receipt?</DialogTitle>
                        <DialogDescription>
                            This removes <span className="font-medium">{removingReceiptLine?.label}</span> from the
                            receipt. The receipt and its other items stay untouched — the line can be added
                            back while the receipt is in progress.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRemovingReceiptLine(null)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            disabled={busy !== null}
                            onClick={async () => {
                                if (!removingReceiptLine) return;
                                await run('rmLine', () => importsApi.deleteReceiptLine(
                                    order.id, removingReceiptLine.receiptId, removingReceiptLine.lineId), 'Line removed');
                                setRemovingReceiptLine(null);
                            }}
                        >
                            <Trash2 size={16} className="mr-2" />Remove Line
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Remove photo dialog — the removal itself is written to the audit trail */}
            <Dialog open={Boolean(removingPhoto)} onOpenChange={(o) => { if (!o) setRemovingPhoto(null); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Remove photo?</DialogTitle>
                        <DialogDescription>
                            The photo comes off this receipt. The removal is written to the audit
                            trail, and the photo can be taken again while the receipt is in progress.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRemovingPhoto(null)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            disabled={busy !== null}
                            onClick={async () => {
                                if (!removingPhoto) return;
                                await run('rmPhoto', () => importsApi.deleteReceiptPhoto(
                                    order.id, removingPhoto.receiptId, removingPhoto.photoId), 'Photo removed');
                                setRemovingPhoto(null);
                            }}
                        >
                            <Trash2 size={16} className="mr-2" />Remove Photo
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete receipt — soft delete for receipts added by accident; the audit trail
                keeps a full snapshot (§6.2). Received receipts are voided, never deleted. */}
            <Dialog open={Boolean(deletingReceipt)} onOpenChange={(o) => { if (!o) setDeletingReceipt(null); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Delete {deletingReceipt?.stockReceiptNumber}?</DialogTitle>
                        <DialogDescription>
                            The receipt — with its lines, photos and signatures — comes off{' '}
                            {order.orderNumber} and no longer counts toward the balance. The audit
                            trail keeps a snapshot of what was deleted. Only delete a receipt that
                            was added by mistake; a received receipt must be voided instead.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeletingReceipt(null)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            disabled={busy !== null}
                            onClick={async () => {
                                if (!deletingReceipt) return;
                                await run(`delReceipt:${deletingReceipt.id}`,
                                    () => importsApi.transitionReceipt(order.id, deletingReceipt.id, { to: 'deleted' }),
                                    'Receipt deleted');
                                setDeletingReceipt(null);
                            }}
                        >
                            {busy === `delReceipt:${deletingReceipt?.id}` && (
                                <Loader2 size={16} className="animate-spin mr-2" />
                            )}
                            Delete Receipt
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add item to import order — receipts can only pick what is on the order (§6.3) */}
            <Dialog open={showAddItem} onOpenChange={(o) => { if (!o) setShowAddItem(false); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Add Item to Import Order</DialogTitle>
                        <DialogDescription>
                            {order.orderNumber} doesn't include this product. Adding it means receipts
                            can be balanced against the ordered quantity.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Product *</Label>
                            <Combobox
                                options={addItemProductOptions}
                                value={addItemProductId}
                                onChange={(v) => setAddItemProductId(v ?? '')}
                                placeholder="Select a product"
                                searchPlaceholder="Search products..."
                                emptyMessage="No matching products"
                            />
                        </div>
                        <div>
                            <Label htmlFor="add-item-qty">Ordered Quantity *</Label>
                            <Input id="add-item-qty" type="number" inputMode="numeric" min="1"
                                   placeholder="Qty" value={addItemQty}
                                   onChange={(e) => setAddItemQty(e.target.value)} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddItem(false)}>Cancel</Button>
                        <Button
                            disabled={busy !== null || !addItemProductId || !(Number(addItemQty) > 0)}
                            onClick={async () => {
                                await run('addOrderLine', () => importsApi.addLine(order.id, {
                                    productId: Number(addItemProductId),
                                    quantity: Number(addItemQty),
                                }), 'Item added to import order');
                                setShowAddItem(false);
                                setAddItemProductId(''); setAddItemQty('');
                            }}
                        >
                            {busy === 'addOrderLine' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Add Item
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Remove a source document — a wrongly attached file can be corrected (§12.4) */}
            <Dialog open={docToDelete !== null} onOpenChange={(o) => { if (!o) setDocToDelete(null); }}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader>
                        <DialogTitle>Remove source document?</DialogTitle>
                        <DialogDescription>
                            Removes {docToDelete?.filename} from {order.orderNumber}. The removal is
                            snapshotted into the audit trail, so who deleted it and when is never lost.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" disabled={busy !== null}
                                onClick={() => setDocToDelete(null)}>Cancel</Button>
                        <Button variant="destructive" disabled={busy !== null}
                                onClick={async () => {
                                    if (!docToDelete) return;
                                    const target = docToDelete;
                                    await run('delDoc',
                                        () => importsApi.deleteDocument(order.id, target.id),
                                        'Document removed');
                                    setDocToDelete(null);
                                }}>
                            {busy === 'delDoc' && <Loader2 size={16} className="animate-spin mr-2" />}
                            Remove document
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ImageLightbox
                alt="Receipt photo"
                images={receiptPhotos}
                index={photoIndex}
                onIndexChange={setPhotoIndex}
            />
        </div>
    );
}
